import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../contexts/AuthContext';
import { useAuth } from '../contexts/AuthContext';
import { BottomNav } from '../components/BottomNav';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { UserAvatar } from '../components/UserAvatar';
import { 
  Calendar, 
  ChevronLeft, 
  ChevronRight, 
  Check, 
  X, 
  Users,
  User,
  UserPlus,
  Trash2,
  ShieldCheck,
  Download
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = (() => {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host !== 'localhost' && !host.includes('preview.emergentagent.com')) return '';
  }
  return process.env.REACT_APP_BACKEND_URL || '';
})();

const DAYS = [
  { id: 0, name: 'Lundi', short: 'LUN' },
  { id: 1, name: 'Mardi', short: 'MAR' },
  { id: 2, name: 'Mercredi', short: 'MER' },
  { id: 3, name: 'Jeudi', short: 'JEU' },
  { id: 4, name: 'Vendredi', short: 'VEN' },
  { id: 5, name: 'Samedi', short: 'SAM' },
  { id: 6, name: 'Dimanche', short: 'DIM' }
];

const STATUS_CONFIG = {
  present: { 
    icon: Check, 
    color: 'text-green-400', 
    bg: 'bg-green-500/20', 
    border: 'border-green-500/50',
    label: 'Present'
  },
  absent: { 
    icon: X, 
    color: 'text-red-400', 
    bg: 'bg-red-500/20', 
    border: 'border-red-500/50',
    label: 'Absent'
  }
};

import { getWeekStartTahiti, formatWeekRange } from '../utils/tahiti';

function ExportAttendanceBtn({ weekStart }) {
  const [loading, setLoading] = useState(false);
  const handleExport = async () => {
    setLoading(true);
    try {
      const dateTo = weekStart;
      const dateFrom = new Date(new Date(weekStart).getTime() - 28 * 86400000).toISOString().split('T')[0];
      const { data } = await apiClient.get(`/attendance/admin/export?date_from=${dateFrom}&date_to=${dateTo}`, { responseType: 'blob' });
      const blob = new Blob([data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `presences_${dateFrom}_${dateTo}.csv`; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error('Erreur export'); }
    finally { setLoading(false); }
  };
  return (
    <button onClick={handleExport} disabled={loading} data-testid="export-attendance"
      className="flex items-center gap-1 px-2 py-1 bg-[#FF6B35]/20 border border-[#FF6B35]/30 rounded text-[10px] font-bold text-[#FF6B35] hover:bg-[#FF6B35]/30 transition-colors">
      <Download className="w-3 h-3" /> {loading ? '...' : 'CSV'}
    </button>
  );
}


function getWeekDates(weekStart) {
  const dates = [];
  const start = new Date(weekStart);
  for (let i = 0; i < 7; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    dates.push(d);
  }
  return dates;
}

export default function PlanningPage({ embedded = false }) {
  const { user } = useAuth();
  const [weekStart, setWeekStart] = useState(getWeekStartTahiti());
  const [myAttendance, setMyAttendance] = useState({ entries: [] });
  const [allAttendances, setAllAttendances] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [showNoteDialog, setShowNoteDialog] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [guests, setGuests] = useState([]);
  const [guestName, setGuestName] = useState('');
  const [guestDay, setGuestDay] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [myRes, allRes, summaryRes, guestsRes] = await Promise.all([
        apiClient.get(`/attendance/my-week?week_start=${weekStart}`),
        apiClient.get(`/attendance/week?week_start=${weekStart}`),
        apiClient.get(`/attendance/summary?week_start=${weekStart}`),
        apiClient.get(`/attendance/guests?week_start=${weekStart}`),
      ]);
      setMyAttendance(myRes.data);
      setAllAttendances(allRes.data);
      setSummary(summaryRes.data.summary || {});
      setGuests(guestsRes.data || []);
    } catch {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSetStatus = async (dayOfWeek, status) => {
    try {
      const currentEntry = myAttendance.entries?.find(e => e.day_of_week === dayOfWeek);
      // Default is present. Member can only declare "absent" (with required note)
      if (status === 'absent') {
        // Force note dialog for absence declaration
        setShowNoteDialog(dayOfWeek);
        setNoteText('');
        return;
      }
      // Clicking present = reset to default (remove entry)
      const newStatus = currentEntry?.status === status ? 'none' : status;
      await apiClient.put(`/attendance/day?day_of_week=${dayOfWeek}&status=${newStatus}&week_start=${weekStart}`);
      toast.success(newStatus === 'none' ? 'Retour au statut par defaut (present)' : 'Presence confirmee');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise a jour');
    }
  };

  const handleAddGuest = async (dayOfWeek) => {
    if (!guestName.trim()) { toast.error('Renseigne le prenom de l\'invite'); return; }
    try {
      await apiClient.post('/attendance/guest', { day_of_week: dayOfWeek, guest_name: guestName.trim(), week_start: weekStart });
      toast.success(`${guestName.trim()} ajoute comme invite`);
      setGuestName('');
      setGuestDay(null);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleRemoveGuest = async (guestId) => {
    try {
      await apiClient.delete(`/attendance/guest/${guestId}`);
      toast.success('Invite retire');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleAdminOverride = async (userId, dayOfWeek, newStatus) => {
    try {
      const { data } = await apiClient.put('/attendance/admin/override', {
        user_id: userId, day_of_week: dayOfWeek, status: newStatus, week_start: weekStart
      });
      toast.success(`${data.user_name} → ${newStatus === 'present' ? 'Present' : 'Absent'} (valide par coach)`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const handleSaveNote = async () => {
    if (showNoteDialog === null) return;
    
    const currentEntry = myAttendance.entries?.find(e => e.day_of_week === showNoteDialog);
    // If note is being added from absence flow, set status to absent
    const currentStatus = currentEntry?.status;
    const isAbsenceFlow = !currentStatus || currentStatus === 'present' || currentStatus === 'none';
    const status = isAbsenceFlow ? 'absent' : currentEntry?.status || 'present';
    
    if (isAbsenceFlow && !noteText.trim()) {
      toast.error('Explique ta raison pour te declarer absent');
      return;
    }
    
    try {
      await apiClient.put(`/attendance/day?day_of_week=${showNoteDialog}&status=${status}&week_start=${weekStart}&note=${encodeURIComponent(noteText)}`);
      toast.success(isAbsenceFlow ? 'Absence declaree — en attente de validation coach' : 'Note enregistree');
      setShowNoteDialog(null);
      setNoteText('');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const getMyStatus = (dayOfWeek) => {
    const entry = myAttendance.entries?.find(e => e.day_of_week === dayOfWeek);
    // Default is "present" — if no entry, member is considered present
    return entry?.status || 'present';
  };

  const getMyNote = (dayOfWeek) => {
    const entry = myAttendance.entries?.find(e => e.day_of_week === dayOfWeek);
    return entry?.note || '';
  };

  const navigateWeek = (direction) => {
    const current = new Date(weekStart);
    current.setDate(current.getDate() + (direction * 7));
    setWeekStart(current.toISOString().split('T')[0]);
  };

  const weekDates = getWeekDates(weekStart);
  const isCurrentWeek = weekStart === getWeekStartTahiti();

  const formatWeekRangeLocal = () => formatWeekRange(weekStart);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className={`${embedded ? '' : 'min-h-screen'} bg-[#0F0F0F] ${embedded ? 'pb-4' : 'pb-safe'}`}>
      {!embedded && (
      <header className="sticky top-0 z-40 bg-[#0F0F0F]/95 backdrop-blur-sm border-b border-[#2A2A2E]">
        <div className="flex items-center justify-between px-4 py-3">
          <div>
            <p className="text-xs text-[#FF6B35] uppercase tracking-widest">Planning</p>
            <h1 className="text-xl font-bold text-white">Présences de la meute</h1>
          </div>
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-zinc-500" />
            <span className="text-sm text-zinc-400">{allAttendances.length} membres</span>
          </div>
        </div>
      </header>
      )}

      <main className="p-4 space-y-6 animate-fade-in">
        {/* Week Navigation */}
        <div className="flex items-center justify-between bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm p-3">
          <button
            onClick={() => navigateWeek(-1)}
            className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-sm transition-colors"
            data-testid="prev-week"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          
          <div className="text-center">
            <p className="text-white font-medium">{formatWeekRangeLocal()}</p>
            {isCurrentWeek && (
              <span className="text-xs text-[#FF6B35]">Semaine actuelle</span>
            )}
          </div>
          
          <button
            onClick={() => navigateWeek(1)}
            className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-sm transition-colors"
            data-testid="next-week"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* My Attendance */}
        <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm overflow-hidden" data-testid="my-attendance">
          <div className="p-4 border-b border-[#2A2A2E] flex items-center gap-2">
            <User className="w-4 h-4 text-[#FF6B35]" />
            <h3 className="text-sm font-medium text-zinc-400">Ma présence</h3>
          </div>
          
          <div className="grid grid-cols-7 divide-x divide-zinc-800">
            {DAYS.map((day, index) => {
              const status = getMyStatus(day.id);
              const date = weekDates[index];
              const isToday = date.toDateString() === new Date().toDateString();
              const entry = myAttendance.entries?.find(e => e.day_of_week === day.id);
              const isAbsent = status === 'absent';
              const isAdminOverride = entry?.admin_override;
              const note = entry?.note;
              
              return (
                <div 
                  key={day.id} 
                  className={`p-2 text-center ${isToday ? 'bg-[#FF6B35]/10' : ''}`}
                >
                  <p className={`text-[10px] font-bold ${isToday ? 'text-[#FF6B35]' : 'text-zinc-500'}`}>
                    {day.short}
                  </p>
                  <p className={`text-xs mb-2 ${isToday ? 'text-white' : 'text-zinc-500'}`}>
                    {date.getDate()}
                  </p>
                  
                  {/* Status display - default is present */}
                  <div className={`p-1.5 rounded-sm mb-1 ${
                    isAbsent ? 'bg-red-500/15 border border-red-500/30' : 'bg-green-500/15 border border-green-500/30'
                  }`}>
                    {isAbsent ? (
                      <X className="w-4 h-4 mx-auto text-red-400" />
                    ) : (
                      <Check className="w-4 h-4 mx-auto text-green-400" />
                    )}
                  </div>
                  
                  {isAdminOverride && <span className="text-[7px] text-[#FFB800]" title="Valide par coach">★ coach</span>}
                  
                  {/* Absent button / note */}
                  {!isAbsent ? (
                    <button
                      onClick={() => handleSetStatus(day.id, 'absent')}
                      className="text-[9px] text-red-400/60 hover:text-red-400 mt-1"
                      data-testid={`declare-absent-${day.id}`}
                    >
                      Absent ?
                    </button>
                  ) : (
                    <button
                      onClick={() => handleSetStatus(day.id, 'present')}
                      className="text-[9px] text-green-400/60 hover:text-green-400 mt-1"
                      data-testid={`back-present-${day.id}`}
                    >
                      Annuler
                    </button>
                  )}
                  
                  {/* Note indicator */}
                  {note && <p className="text-[7px] text-zinc-500 truncate mt-0.5" title={note}>{note.slice(0, 15)}</p>}

                  {/* Add guest button */}
                  <button onClick={() => setGuestDay(guestDay === day.id ? null : day.id)} className="text-[9px] text-[#3b82f6] hover:text-[#60a5fa] mt-0.5 flex items-center justify-center gap-0.5" data-testid={`add-guest-${day.id}`}>
                    <UserPlus className="w-3 h-3" /> +1
                  </button>
                  {guestDay === day.id && (
                    <div className="mt-1 space-y-1">
                      <input value={guestName} onChange={e => setGuestName(e.target.value)} placeholder="Prenom"
                        className="w-full bg-[#0F0F0F] border border-[#2A2A2E] text-white rounded text-[10px] px-1 py-0.5" data-testid={`guest-name-${day.id}`}
                        onKeyDown={e => { if (e.key === 'Enter') handleAddGuest(day.id); }} />
                      <button onClick={() => handleAddGuest(day.id)} className="w-full text-[9px] bg-[#3b82f6] text-white rounded py-0.5">OK</button>
                    </div>
                  )}

                  {/* My guests for this day */}
                  {guests.filter(g => g.day_of_week === day.id && g.member_id === user?.id).map(g => (
                    <div key={g.id} className="flex items-center justify-center gap-0.5 mt-0.5">
                      <span className="text-[8px] text-[#3b82f6] truncate">{g.guest_name}</span>
                      <button onClick={() => handleRemoveGuest(g.id)} className="text-red-400 hover:text-red-300"><Trash2 className="w-2.5 h-2.5" /></button>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>

        {/* Summary */}
        <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm p-4" data-testid="attendance-summary">
          <h3 className="text-sm font-medium text-zinc-400 mb-3 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[#FF6B35]" />
            Résumé de la semaine
          </h3>
          
          <div className="grid grid-cols-7 gap-2">
            {DAYS.map(day => {
              const daySummary = summary[day.id] || { present: 0, absent: 0, guests: [] };
              const dayGuests = daySummary.guests || [];
              const total = daySummary.present;
              
              return (
                <div key={day.id} className="text-center">
                  <p className="text-xs text-zinc-500 mb-1">{day.short}</p>
                  <div className={`py-2 rounded-sm ${total > 0 ? 'bg-[#FF6B35]/20' : 'bg-zinc-800'}`}>
                    <p className={`font-mono text-lg font-bold ${total > 0 ? 'text-[#FF6B35]' : 'text-zinc-500'}`}>
                      {total}
                    </p>
                    <p className="text-xs text-zinc-500">{daySummary.absent} abs.</p>
                    {dayGuests.length > 0 && (
                      <p className="text-[8px] text-[#3b82f6] mt-0.5">{dayGuests.length} invite{dayGuests.length > 1 ? 's' : ''}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* All Members */}
        <div className="bg-[#1B1B1B] border border-[#2A2A2E] rounded-sm" data-testid="all-members">
          <div className="p-4 border-b border-[#2A2A2E] flex items-center justify-between">
            <h3 className="text-sm font-medium text-zinc-400 flex items-center gap-2">
              <Users className="w-4 h-4 text-[#FF6B35]" />
              Membres de la meute
            </h3>
            <div className="flex items-center gap-2">
              <span className="text-xs text-zinc-500">{allAttendances.length} membre{allAttendances.length > 1 ? 's' : ''}</span>
              {user?.role === 'admin' && <ExportAttendanceBtn weekStart={weekStart} />}
            </div>
          </div>
          
          {allAttendances.length > 0 ? (
            <div className="divide-y divide-zinc-800">
              {allAttendances.map((attendance, idx) => (
                <div key={attendance.id || idx} className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <UserAvatar name={attendance.user_name} photoPath={attendance.profile_photo} size="sm" />
                    <div>
                      <p className="text-white font-medium text-sm">{attendance.user_name}</p>
                      <p className="text-xs text-zinc-500">{attendance.user_email}</p>
                    </div>
                  </div>
                  
                  {/* Week grid */}
                  <div className="grid grid-cols-7 gap-1">
                    {DAYS.map(day => {
                      const entry = attendance.entries?.find(e => e.day_of_week === day.id);
                      const status = entry?.status || 'present';  // Default present
                      const isAbsent = status === 'absent';
                      const isOverride = entry?.admin_override;
                      const note = entry?.note;
                      
                      return (
                        <div 
                          key={day.id}
                          className={`p-2 rounded-sm text-center relative ${
                            isAbsent ? 'bg-red-500/10 border border-red-500/20' : 'bg-green-500/10 border border-green-500/20'
                          }`}
                        >
                          <p className="text-[9px] text-zinc-400 mb-1">{day.short}</p>
                          {isAbsent ? (
                            <X className="w-3.5 h-3.5 mx-auto text-red-400" />
                          ) : (
                            <Check className="w-3.5 h-3.5 mx-auto text-green-400" />
                          )}
                          {isOverride && <span className="absolute top-0.5 right-0.5 text-[7px] text-[#FFB800]" title="Valide par coach">★</span>}
                          {note && <p className="text-[6px] text-zinc-600 truncate mt-0.5" title={note}>{note.slice(0, 10)}</p>}
                          {/* Admin override buttons */}
                          {user?.role === 'admin' && isAbsent && !isOverride && (
                            <div className="flex gap-0.5 justify-center mt-1">
                              <button onClick={() => handleAdminOverride(attendance.user_id, day.id, 'present')}
                                className="text-[7px] bg-green-600/80 text-white px-1.5 py-0.5 rounded hover:bg-green-700 flex items-center gap-0.5"
                                data-testid={`override-present-${attendance.user_id}-${day.id}`}
                                title="Valider presence">
                                <ShieldCheck className="w-2.5 h-2.5" /> OK
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <Users className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
              <p className="text-zinc-400">Aucune présence enregistrée</p>
              <p className="text-sm text-zinc-500">Soyez le premier à indiquer votre présence !</p>
            </div>
          )}

          {/* Guests section */}
          {guests.length > 0 && (
            <div className="p-4 border-t border-[#2A2A2E]">
              <h4 className="text-xs text-[#3b82f6] font-bold mb-2 flex items-center gap-1">
                <UserPlus className="w-3 h-3" /> Invites de la semaine ({guests.length})
              </h4>
              <div className="space-y-1">
                {guests.map(g => (
                  <div key={g.id} className="flex items-center gap-2 text-xs px-2 py-1 bg-[#0F0F0F] rounded" data-testid={`guest-entry-${g.id}`}>
                    <span className="text-white">{g.guest_name}</span>
                    <span className="text-zinc-500">(invite de {g.member_name})</span>
                    <span className="text-[9px] text-zinc-600">{DAYS[g.day_of_week]?.short}</span>
                    {(g.member_id === user?.id || user?.role === 'admin') && (
                      <button onClick={() => handleRemoveGuest(g.id)} className="ml-auto text-red-400 hover:text-red-300" data-testid={`remove-guest-${g.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Note/Absence Dialog */}
      <Dialog open={showNoteDialog !== null} onOpenChange={() => setShowNoteDialog(null)}>
        <DialogContent className="bg-[#1B1B1B] border-[#2A2A2E] text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white">
              Declarer absence — {showNoteDialog !== null ? DAYS[showNoteDialog]?.name : ''}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-zinc-400 -mt-2">Explique la raison de ton absence. Le coach validera.</p>
          
          <Textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Raison de l'absence (obligatoire)..."
            className="bg-[#0F0F0F] border-[#2A2A2E] text-white min-h-[80px]"
            data-testid="note-input"
          />

          <DialogFooter className="flex gap-2 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowNoteDialog(null)}
              className="flex-1 border-zinc-700 text-zinc-400 hover:bg-zinc-800"
            >
              Annuler
            </Button>
            <Button
              onClick={handleSaveNote}
              disabled={!noteText.trim()}
              className="flex-1 bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              data-testid="save-note-button"
            >
              Confirmer absence
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {!embedded && <BottomNav />}
    </div>
  );
}
